import React, { useState } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Avatar, Segmented, Area, Field } from "@/components/kit";
import { relTime } from "@/lib/util";

export default function Feed() {
  const { me, feed, memberById, yearId, refresh, canWrite } = useTable();
  const [type, setType] = useState("Update");
  const [body, setBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const post = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/feed", { yearId, type, body, linkUrl });
      setBody(""); setLinkUrl("");
      await refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="feed-page">
      <PageHead kicker="Between chapters" title="Feed" sub="Short signals between sittings. Updates, wins, asks." />

      {canWrite && (
        <Card className="mb-6">
          <Segmented options={["Update", "Win", "Ask"]} value={type} onChange={setType} testId="feed-type" />
          <div className="mt-4 space-y-3">
            <Area placeholder="Say it plainly." value={body} data-testid="feed-body" onChange={(e) => setBody(e.target.value)} />
            <Field placeholder="Link (optional)" value={linkUrl} data-testid="feed-link" onChange={(e) => setLinkUrl(e.target.value)} />
            <button className="btn-ink" data-testid="feed-post" onClick={post} disabled={!body.trim()}>Post</button>
          </div>
        </Card>
      )}

      {feed.length === 0 ? (
        <Empty line="Quiet so far. First post sets the tone." testId="feed-empty" />
      ) : (
        <div className="space-y-3">
          {feed.map((p) => {
            const a = memberById(p.authorId);
            return (
              <Card key={p.id} className="flex gap-3" data-testid={`feed-post-${p.id}`}>
                <Avatar name={a?.name || "—"} color={a?.avatarColor} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm">{a?.name || "Member"}</span>
                    <Chip color={p.type === "Win" ? "var(--green)" : p.type === "Ask" ? "var(--rust)" : undefined}>{p.type}</Chip>
                    <span className="text-xs" style={{ color: "var(--ink-mute)" }}>{relTime(p.createdAt)}</span>
                  </div>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{p.body}</p>
                  {p.linkUrl && (
                    <a href={p.linkUrl} target="_blank" rel="noreferrer" className="text-xs mt-2 inline-block underline" style={{ color: "var(--brass)" }}>
                      {p.linkUrl}
                    </a>
                  )}
                  {(me.isAdmin || p.authorId === me.memberId) && canWrite && (
                    <button
                      className="btn-quiet mt-3"
                      data-testid={`feed-delete-${p.id}`}
                      onClick={async () => { await api.delete(`/feed/${p.id}`); await refresh(); }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
